/**
 * Grid Layout Switcher
 * Handles switching between single and double column layouts
 * Only active on devices under 989px (mobile/tablet)
 */

class GridLayoutSwitcher {
  constructor() {
    this.productGrid = null
    this.buttons = []
    this.currentLayout = 'double'
    this.originalGridClasses = null
    this.isActive = false
    this.attributeKey = 'grid_layout'
    this.init()
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners())
    } else {
      this.setupEventListeners()
    }
  }

  isMobileTablet() {
    return window.innerWidth < 989
  }

  readInitialLayout() {
    const fromGrid =
      this.productGrid && this.productGrid.dataset.gridLayoutCurrent
    if (fromGrid === 'single' || fromGrid === 'double') return fromGrid

    const activeButton = document.querySelector(
      '[data-grid-layout].active, [data-grid-layout][aria-pressed="true"]',
    )
    if (activeButton) {
      const layout = activeButton.dataset.gridLayout
      if (layout === 'single' || layout === 'double') return layout
    }

    return 'double'
  }

  setupEventListeners() {
    this.buttons = document.querySelectorAll('[data-grid-layout]')
    this.productGrid = document.querySelector(
      '#product-grid, .col-ProductGrid_Items, .product-grid',
    )

    if (!this.productGrid || this.buttons.length === 0) {
      return
    }

    this.currentLayout = this.readInitialLayout()

    this.buttons.forEach((button) => {
      button.addEventListener('click', (e) => this.handleLayoutChange(e))
    })

    window.addEventListener('resize', () => this.handleResize())

    this.handleResize()
    this.observeGridChanges()
  }

  handleResize() {
    const shouldBeActive = this.isMobileTablet()

    if (shouldBeActive && !this.isActive) {
      this.isActive = true
      this.applyLayout(this.currentLayout)
      this.updateButtonStates()
    } else if (!shouldBeActive) {
      this.isActive = false
      this.removeGridLayoutClasses()
    }
  }

  removeGridLayoutClasses() {
    if (this.productGrid) {
      this.productGrid.classList.remove(
        'product-grid--single-column',
        'product-grid--double-column',
      )
    }
  }

  handleLayoutChange(event) {
    if (!this.isActive) return

    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const newLayout = button.dataset.gridLayout

    if (newLayout && newLayout !== this.currentLayout) {
      this.applyLayout(newLayout)
      this.updateButtonStates()
      this.persistLayout(newLayout)
    }
  }

  applyLayout(layout) {
    if (!this.productGrid || !this.isActive) return

    this.productGrid.classList.remove(
      'product-grid--single-column',
      'product-grid--double-column',
    )

    if (!this.originalGridClasses) {
      this.originalGridClasses = Array.from(this.productGrid.classList).filter(
        (cls) => cls.includes('grid--') && !cls.includes('product-grid--'),
      )
    }

    if (layout === 'single') {
      this.productGrid.classList.add('product-grid--single-column')
      this.currentLayout = 'single'
      this.productGrid.style.setProperty('--ProductGrid-Columns-Mobile', '1')
    } else {
      this.productGrid.classList.add('product-grid--double-column')
      this.currentLayout = 'double'
      this.productGrid.style.setProperty('--ProductGrid-Columns-Mobile', '2')
    }

    this.productGrid.dataset.gridLayoutCurrent = this.currentLayout

    this.productGrid.dispatchEvent(
      new CustomEvent('gridLayoutChanged', {
        detail: {
          layout: this.currentLayout,
          previousLayout: layout === 'single' ? 'double' : 'single',
          isActive: this.isActive,
        },
      }),
    )

    this.refreshProductCardCarousels()
  }

  refreshProductCardCarousels () {
    const items = document.querySelectorAll(
      '#ProductGridContainer .grid__item, #ProductGridContainer .col-ProductGrid_Item',
    )

    items.forEach((item) => {
      item.style.transition = 'none'
    })

    // Width transitions finish after Keen's first measure, which leaves slides at the old size.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.querySelectorAll('product-card-carousel').forEach((carouselEl) => {
          if (carouselEl.carousel && typeof carouselEl.carousel.update === 'function') {
            carouselEl.carousel.update()
          }
        })

        items.forEach((item) => {
          item.style.transition = ''
        })
      })
    })
  }

  async persistLayout(layout) {
    try {
      if (
        window.BAO &&
        window.BAO.Cart &&
        typeof window.BAO.Cart.updateCartAttributes === 'function'
      ) {
        await window.BAO.Cart.updateCartAttributes(
          { [this.attributeKey]: layout },
          { sections: [] },
        )
        return
      }
    } catch (e) {
    }

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            [this.attributeKey]: layout,
          },
        }),
      })
    } catch (e) {
    }
  }

  updateButtonStates() {
    this.buttons.forEach((button) => {
      const layout = button.dataset.gridLayout

      if (layout === this.currentLayout) {
        button.classList.add('active')
        button.setAttribute('aria-pressed', 'true')
      } else {
        button.classList.remove('active')
        button.setAttribute('aria-pressed', 'false')
      }
    })
  }

  syncGrid() {
    this.productGrid = document.querySelector(
      '#product-grid, .col-ProductGrid_Items, .product-grid',
    )
    this.buttons = document.querySelectorAll('[data-grid-layout]')

    if (!this.productGrid || this.buttons.length === 0) {
      this.refreshProductCardCarousels()
      return
    }

    this.observeGridChanges()

    if (!this.isMobileTablet()) return

    this.isActive = true
    this.applyLayout(this.currentLayout)
    this.updateButtonStates()
  }

  observeGridChanges() {
    if (!window.MutationObserver || !this.productGrid) return

    this.gridObserver?.disconnect()

    this.gridObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          if (this.isActive) {
            this.applyLayout(this.currentLayout)
          }
        }
      })
    })

    this.gridObserver.observe(this.productGrid, {
      childList: true,
      subtree: true,
    })
  }

  getCurrentLayout() {
    return this.currentLayout
  }

  isActivated() {
    return this.isActive
  }

  switchTo(layout) {
    if (!this.isActive) return

    if (layout === 'single' || layout === 'double') {
      this.applyLayout(layout)
      this.updateButtonStates()
      this.persistLayout(layout)
    }
  }
}

let gridLayoutSwitcher

function initGridLayoutSwitcher() {
  gridLayoutSwitcher = new GridLayoutSwitcher()
  window.gridLayoutSwitcher = gridLayoutSwitcher
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGridLayoutSwitcher)
} else {
  initGridLayoutSwitcher()
}

document.addEventListener('shopify:section:load', () => {
  setTimeout(initGridLayoutSwitcher, 100)
})

window.gridLayoutSwitcher = gridLayoutSwitcher
